import type { Event, Ref } from "@melo/shared";
import { api, ApiError } from "./api";
import { store } from "./store";
import { toast } from "./toast";

const MAX = 40 * 1024 * 1024;

/** Uploads files as references on a space or an item. Uploads run a few at a time. */
export async function uploadFiles(files: File[], target: { spaceId: string; nodeId?: string | null; tags?: string[] }): Promise<Ref[]> {
  const ok = files.filter((f) => f.size <= MAX);
  if (ok.length < files.length) toast(`${files.length - ok.length} file(s) are over 40 MB and were skipped.`, { tone: "error" });
  if (!ok.length) return [];
  toast(ok.length === 1 ? `Uploading ${ok[0]!.name}` : `Uploading ${ok.length} files`);
  const out: Ref[] = [];
  const queue = [...ok];
  const worker = async () => {
    for (let f = queue.shift(); f; f = queue.shift()) {
      const form = new FormData();
      form.set("file", f);
      form.set("space", target.spaceId);
      if (target.nodeId) form.set("node", target.nodeId);
      if (target.tags?.length) form.set("tags", target.tags.join(","));
      try {
        const res = await api.upload<{ ref: Ref; events: Event[] }>("/v1/refs/upload", form);
        store.applyEvents(res.events);
        out.push(res.ref);
      } catch (e) {
        toast(`${f.name}: ${e instanceof ApiError ? e.message : "upload failed."}`, { tone: "error" });
      }
    }
  };
  await Promise.all([worker(), worker(), worker()]);
  if (out.length) toast(out.length === 1 ? `Added ${out[0]!.title || "the file"}` : `Added ${out.length} files`);
  return out;
}

/** Files from a drop or paste event, if any. */
export function filesFrom(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  return [...dt.files];
}
